import { FormikErrors } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route } from '~/Constants/Routes.constants';
import { isObjectEmpty } from '../../Helpers/CleanData.helper';
import {
  useDebouncedSearch,
  useLanguage,
  useLocalStorage,
  useNavigation,
  useProviderCategoryList,
  useUser,
} from '../../Hooks';
import { useRequest } from '../../Hooks/useRequest';
import { IErrorResponse } from '../../Models/DTO/Error.models';
import {
  checkStudioName,
  verifyEmail,
  resendEmailVerification,
  upgradeCustomerToAProviderAccount,
} from './helpers/APIs';
import {
  createAProviderAccount,
  createAUserAndProvider,
  createAProviderPostLogin,
} from './helpers/createAUserAndProvider';
import { validateStudioData } from './helpers/useVerification';
import { IOnboardingData, onboardingPage, CreationSource } from './IOnboardingData.model';
import { IProviderCreation } from './models/providerCreation.models';
import { IStudioName } from './models/studioName.models';
import { IUserCreation } from './models/userCreation.models';
import { IVerifyEmail } from './models/verifyEmail.model';
import { useAuth } from '~/store/auth';
import { Segment } from '~/Helpers/Segment.helper';
import { useRequestCitiesCountries } from '~/requests';
import { requestProviderById } from '~/store/provider';

const {
  LOGIN,
  ONBOARDING,
  ONBOARDING_DETAILS,
  ONBOARDING_STUDIO,
  ONBOARDING_VERIFICATION,
  PROVIDER_DETAIL,
} = Route;

const defaultValues: IOnboardingData = {
  aggregatorType: '',
  countryName: '',
  studioName: '',
  studioType: '',
  studioSlug: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  isProvider: false,
  isUser: false,
  termsAndConditions: false,
  dpa: false,
  privacyPolicy: false,
  organisationId: '',
  creationSource: CreationSource.Fitogram,
};

const defaultProvider: IProviderCreation = {
  email: '',
  name: '',
  providerCategoryId: 16,
  timeZoneId: '',
  isUSCMember: false,
  isOneFitMember: false,
  slug: '',
  creationSource: CreationSource.Fitogram,
  mainLocation: {
    name: '',
    city: '',
    country: '',
    latitude: 0,
    longitude: 0,
    street: '',
    zip_code: '',
  },
};

interface IUseOnboarding {
  postLogin?: boolean;
}
export const useOnboarding = ({ postLogin = false } = {} as IUseOnboarding) => {
  const dispatch = useDispatch();
  // states
  const [userData, setUserData] = useState<IUserCreation>({ new_user: {} });
  const [providerData, setProviderData] = useState<IProviderCreation>(defaultProvider);
  const [page, setPage] = useState<onboardingPage>(onboardingPage.STUDIO);

  const [networkError, setNetworkError] = useState<IErrorResponse>({});
  const [studioNameCheck, setStudioNameCheck] = useState<IStudioName>({});

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isStudioSetupDone, setIsStudioSetupDone] = useState(false);
  const [isStudioDetailsDone, setIsStudioDetailsDone] = useState(false);
  const [isAlreadyAProvider, setIsAlreadyAProvider] = useState(false);
  const [isAlreadyAUser, setIsAlreadyAUser] = useState(false);
  const [studioNameContainsAHyperLink, setStudioNameContainsAHyperLink] = useState(false);
  const [isPasswordWrong, setIsPasswordWrong] = useState(false);

  // hooks
  const { history, pushBack, navigate } = useNavigation();
  const { requestWithCallback } = useRequest();
  const { searchResults, setInputText } = useDebouncedSearch((text: string) => text);
  const { currentLanguage } = useLanguage();
  const { categoryList, categoryListError, isCategoryListLoading } = useProviderCategoryList();
  const { t } = useTranslation();
  const { login, loginWithNoToken } = useAuth();
  const { currentUser } = useUser();
  const { accessToken } = useLocalStorage();

  // actions
  const { data: countryList = [] } = useRequestCitiesCountries({});
  const getProviderById = bindActionCreators(requestProviderById, dispatch);

  const validateForm: (
    values: IOnboardingData,
  ) => void | object | Promise<FormikErrors<IOnboardingData>> = (data: IOnboardingData) => {
    if (page === onboardingPage.STUDIO) {
      setInputText(data.studioName);
      const errors = validateStudioData(
        t,
        data,
        setProviderData,
        studioNameCheck,
        studioNameContainsAHyperLink,
        postLogin,
        countryList,
      );
      setIsStudioSetupDone(
        isObjectEmpty(errors) && !searchResults.loading && !isObjectEmpty(studioNameCheck),
      );
      return errors;
    } else if (page === onboardingPage.DETAILS) {
      setInputText(data.email);
      setUserData({
        new_user: {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          password: data.password,
          password_confirmation: data.password,
          ulanguage: currentLanguage,
          utype: 'pro',
        },
      });
      setProviderData({
        ...providerData,
        email: data.email,
        slug: categoryList.find((cat) => cat.id === providerData.providerCategoryId)?.slug || '',
      });
      setIsStudioDetailsDone(!searchResults.loading);
      return {};
    }
  };

  // check if we are at the correct page, and set it.
  useEffect(() => {
    switch (history?.location?.pathname) {
      case ONBOARDING:
        pushBack(ONBOARDING_STUDIO);
        break;
      case ONBOARDING_STUDIO:
        if (!!accessToken) {
          navigate(ONBOARDING_VERIFICATION);
          break;
        }
        setPage(onboardingPage.STUDIO);
        break;
      case ONBOARDING_DETAILS:
        if (!!accessToken) {
          navigate(ONBOARDING_VERIFICATION);
          break;
        }
        if (!providerData.name) {
          pushBack(ONBOARDING_STUDIO);
        } else {
          setPage(onboardingPage.DETAILS);
        }
        break;
      case ONBOARDING_VERIFICATION:
        setPage(onboardingPage.VERIFY_EMAIL);
        break;
    }
  }, [history?.location?.pathname, accessToken]);

  // when the studio name changes, check if it is valid
  useEffect(() => {
    if (!searchResults.loading && searchResults.value) {
      if (page === onboardingPage.STUDIO) {
        checkStudioName(
          requestWithCallback,
          searchResults.value,
          setStudioNameCheck,
          setStudioNameContainsAHyperLink,
        );
      }
      if (page === onboardingPage.DETAILS) {
        verifyEmail(requestWithCallback, searchResults.value, checkProviderStatus, setNetworkError);
      }
    }
  }, [searchResults.value]);

  const checkProviderStatus = (verifyEmail: IVerifyEmail) => {
    if (verifyEmail.emailStatus === 'admin' || verifyEmail.emailStatus === 'provider') {
      setIsAlreadyAUser(false);
      setIsAlreadyAProvider(true);
    }
    if (verifyEmail.emailStatus === 'customer') {
      setIsAlreadyAUser(true);
      setIsAlreadyAProvider(false);
    }
    if (verifyEmail.emailStatus === 'new') {
      setIsAlreadyAUser(false);
      setIsAlreadyAProvider(false);
    }
  };

  const onDoneSettingUpStudio = () => {
    setInputText('');
    setStudioNameCheck({});
    pushBack(ONBOARDING_DETAILS);
  };

  const onCreateProviderPostLogin = async () => {
    if (postLogin) {
      createAProviderPostLogin(
        requestWithCallback,
        { ...providerData, accessType: 'PartnerTool' },
        async ({ provider: newProvider }: any) => {
          const { uuid, domain } = newProvider;
          Segment.track('signup');
          getProviderById(uuid);
          navigate(PROVIDER_DETAIL, { domain: domain || '' });
        },
        (error) => setNetworkError(error),
        accessToken,
      );
    }
  };
  const onCreateProviderAccount = async () => {
    if (isAlreadyAUser) {
      const data = await loginWithNoToken({
        username: userData.new_user.email || '',
        password: userData.new_user.password || '',
      });
      if ((data as any).error) {
        setIsPasswordWrong(true);
        return;
      } else {
        setIsPasswordWrong(false);
      }
      createAProviderAccount(
        requestWithCallback,
        userData,
        providerData,
        async () => {
          Segment.track('signup');
          upgradeCustomerToAProviderAccount(
            requestWithCallback,
            () => {},
            (error) => setNetworkError(error),
            (data as any).access_token,
          );
          await login({
            username: userData.new_user.email || '',
            password: userData.new_user.password || '',
          });
        },
        (error) => setNetworkError(error),
        (data as any).access_token,
      );
    } else {
      createAUserAndProvider(
        requestWithCallback,
        userData,
        providerData,
        currentUser,
        async () => {
          Segment.track('signup');
          await login({
            username: userData.new_user.email || '',
            password: userData.new_user.password || '',
          });
        },
        (error) => setNetworkError(error),
      );
    }
  };

  const onRedirectToLogin = () => {
    navigate(LOGIN);
  };

  const onResendEmail = (uuid: string) => {
    setLoading(true);
    resendEmailVerification(
      requestWithCallback,
      uuid,
      () => {
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        setNetworkError(error);
      },
    );
  };

  return {
    initialValues: {
      ...defaultValues,
      locationName: postLogin ? '' : undefined,
      creationSource: postLogin ? CreationSource.PartnerPortal : CreationSource.Fitogram,
    },
    validateForm,
    page,
    countryList,
    categoryList: categoryList.map((SPC) => SPC.id),
    networkError: networkError || categoryListError,
    isStudioSetupDone,
    isStudioDetailsDone,
    isAlreadyAProvider,
    isAlreadyAUser,
    onDoneSettingUpStudio,
    onCreateProviderAccount,
    onRedirectToLogin,
    onResendEmail,
    providerData,
    userData,
    isLoading: isLoading || isCategoryListLoading,
    isPasswordWrong,
    onCreateProviderPostLogin,
  };
};
